import { connect, Dispatch } from 'react-redux';
import { AppState } from 'src';
import {
  OrderEditComponent,
  OrderEditProperties
} from 'src/contract/_components/order-edit/_components/order-edit.component';
import { cloneContract } from 'src/contract/contract.actions';
import { blankStatesVM, AddressViewModel } from '@models/common.models';
import {
  setOrderEditLoading,
  saveOrderEdit,
  setIsStoringPropertyInfo,
  setIsStoringPriceAdjustmentInfo,
  setIsStoringCoverageInfo,
  setIsStoringAffiliatesInfo
} from 'src/contract/_components/order-edit/order-edit.actions';
import { doAsync, registerAddressVerifier, toggleModal } from '@common/components/common.actions';
import FormNames from '@constants/FormNames.enum';
import { GetPriceAdjustmentMetadata } from '@models/Product';
import {
  triggerGetPriceAdjustments,
  triggerGetCoverageOptions,
  getEmployeeDiscount,
  updateSelections,
  updateTotalPrice,
  getProductGroups,
  triggerGetProducts,
  setSellersCoverage
} from 'src/contract/_components/coverage';
import { isSellersCoverageRequiredState } from '@common/validators';
import { actions } from 'react-redux-form';
import { ContractEmail } from '../../contract-email/_components/contract-email.container';

enum TabNumbers {
  PropertyInfo = 0,
  Coverage = 1,
  Customer = 2,
  Affiliates = 3,
  PriceAdjustments = 4
}

/*
This is the container for the component.
This "connects" the redux and react layers by translating the state of the page into props for the component.
It also wires up the actions so the component can emit them.
*/

// This is used to translate the state of the page to the props on the component
function mapStateToProps(state: AppState): Partial<OrderEditProperties> {
  let contractVM = state.contract.contractVM;
  return {
    contractVM: state.contract.contractVM!,
    isEmployeeDiscountEnabled: state.coverageStep.isEmployeeDiscountEnabled,
    saleTypeId: contractVM && contractVM.propertySaleType ? contractVM.propertySaleType.id : 0,
    state: contractVM ? contractVM!.propertyAddress.state || blankStatesVM : blankStatesVM,
    propertyTypeId: contractVM && contractVM.propertyType ? contractVM!.propertyType.id : 0,
    hasPropertyManagement: state.contract.propertyManager !== undefined,
    hasAgent: contractVM ? contractVM!.initAgentId !== undefined : false,
    loading: state.contract.orderEdit.isLoadingContract,
    addressChanged: state.forms.propertyInfoForm && state.forms.propertyInfoForm.propertyAddress.$form.touched,
    checkIsTabValid: (fromTab: number) => {
      const map = {
        [TabNumbers.PropertyInfo]: FormNames.PropertyInfoForm,
        [TabNumbers.Coverage]: FormNames.CoverageForm,
        [TabNumbers.Customer]: FormNames.CustomerInfoForm,
        [TabNumbers.Affiliates]: FormNames.AffiliatesInfoForm,
        [TabNumbers.PriceAdjustments]: FormNames.PriceAdjustmentForm
      };
      const form = state.forms[map[fromTab]].$form;
      return form.valid && !form.validating;
    },
    isOpenContract: state.contract.isOpenContract,
    commentText:
      state.forms &&
      state.forms.commentForm &&
      state.forms.commentForm.comment &&
      state.forms.commentForm.comment.value,
    commentCallerName:
      state.forms &&
      state.forms.commentForm &&
      state.forms.commentForm.callerName &&
      state.forms.commentForm.callerName.value
  };
}

// This should create proxy wrappers for actions used by the component
function mapDispatchToProps(dispatch: Dispatch<AppState>): Partial<OrderEditProperties> {
  return {
    loadOrderEditContract: (id: number) => {
      dispatch(actions.reset(FormNames.CommentForm));
      dispatch(doAsync(setOrderEditLoading(true))).then(() => {
        dispatch(cloneContract(id, true)).then(() => {
          dispatch(setOrderEditLoading(false));
        });
      });
    },
    loadCoverageStep: (
      adjustmentMetaData: GetPriceAdjustmentMetadata,
      propertyTypeId: number,
      saleTypeId: number,
      isRenewal: boolean
    ) => {
      let dropdownsPromise = Promise.all([
        dispatch(getProductGroups(adjustmentMetaData.zipCode, propertyTypeId, saleTypeId)),
        dispatch(triggerGetProducts(propertyTypeId, saleTypeId, isRenewal)),
        dispatch(triggerGetPriceAdjustments(adjustmentMetaData)),
        dispatch(triggerGetCoverageOptions(isRenewal))
      ]);

      if (adjustmentMetaData.customerId) {
        dispatch(getEmployeeDiscount(adjustmentMetaData.customerId));
      }
      if (isSellersCoverageRequiredState(adjustmentMetaData.stateAbbreviation)) {
        dispatch(setSellersCoverage(true));
      }

      dropdownsPromise.then(() => {
        return dispatch(updateSelections()).then(() => {
          dispatch(updateTotalPrice());
        });
      });
    },
    loadPriceAdjustmentStep: (adjustmentMetaData: GetPriceAdjustmentMetadata, isRenewal: boolean) => {
      let dropdownsPromise = Promise.all([
        dispatch(triggerGetPriceAdjustments(adjustmentMetaData)),
        dispatch(triggerGetCoverageOptions(isRenewal))
      ]);

      if (adjustmentMetaData.customerId) {
        dispatch(getEmployeeDiscount(adjustmentMetaData.customerId));
      }
      dropdownsPromise.then(() => {
        return dispatch(updateSelections()).then(() => {
          dispatch(updateTotalPrice());
        });
      });
    },
    validateComment: async () => {
      await dispatch(actions.blur(`${FormNames.CommentForm}.comment`));
      await dispatch(actions.blur(`${FormNames.CommentForm}.callerName`));
    },
    saveContractOrder: async (id: number) => {
      const afterSave = () => {
        const callback = () => {
          dispatch(toggleModal());
        };
        dispatch(
          toggleModal({
            title: 'Select Contract Notifications',
            component: ContractEmail,
            childProps: {
              contractId: id,
              saveCallback: callback
            },
            closeLabel: 'Close',
            hideX: true,
            saveLabel: 'Continue',
            closeCallback: callback,
            saveCallback: callback
          })
        );
      };
      return await dispatch(saveOrderEdit(id, afterSave));
    },
    setIsStoringStep: async (isStoring: boolean, stepNumber: number) => {
      switch (stepNumber) {
        case 0:
          await dispatch(setIsStoringPropertyInfo(isStoring));
          break;
        case 1:
          await dispatch(setIsStoringCoverageInfo(isStoring));
          break;
        case 3:
          await dispatch(setIsStoringAffiliatesInfo(isStoring));
          break;
        case 4:
          await dispatch(setIsStoringPriceAdjustmentInfo(isStoring));
          break;
        default:
          break;
      }
    },
    verifyAddress: async (callback: Function) => {
      try {
        var result = await dispatch(
          registerAddressVerifier(
            FormNames.PropertyInfoForm,
            'propertyAddress',
            (s: any, a: AddressViewModel) => {
              return {
                ...s,
                propertyAddress: {
                  ...a
                }
              };
            },
            false,
            callback
          )
        );
        return result;
      } catch (e) {
        // do nothin
        return null;
      }
    }
  };
}

// Connect function "wires" both of the map functions and returns a wrapper container for the component
export const OrderEdit = connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderEditComponent);



// WEBPACK FOOTER //
// ./src/contract/_components/order-edit/_components/order-edit.container.ts